import React, { useCallback, useMemo, useState } from 'react';
import {
  Drawer,
  alpha,
  IconButton,
  Button,
  Typography,
  List
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import AddIcon from '@mui/icons-material/Add';
import { FormattedMessage } from 'react-intl';
import { useDialogState, useFeatureFlag } from 'modules/common/hooks';
import { TimesheetLabelSuggestionContextProvider } from 'modules/timesheet-labels';
import { AddNoteDialog } from 'modules/notes/addNote/AddNoteDialog';
import { NoteAnimationContextProvider } from 'modules/notes/providers';
import { PinIcon } from 'modules/notes/icons/PinIcon';
import { NoteToast } from 'modules/notes/NoteToast';
import { useMessage } from 'modules/intl';
import { useAutoPilotContext } from 'modules/App/menu/items/Settings/AutoPilotContextProvider';
import { ReactComponent as ZT_logo_Expanded } from './icons/ZeroTime_Logo_Nav_Expanded.svg';
import { ReactComponent as ZT_logo_Collapsed } from './icons/ZeroTime_Logo_Nav_Collapsed.svg';
import { useSidePanelContext } from './SidePanelContext';
import { SidePanelOptions } from './SidePanelOptions';
import { UserProfile } from './UserProfile';
import { AutoPilotEnabled } from './AutoPilot';

type PageStyleProps = {
  pinned: boolean;
  drawerOpen: boolean;
};

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    display: 'block',
    backgroundColor: theme.palette.background.paper,
    border: 'none',
    whiteSpace: 'nowrap',
    width: theme.spacing(30),
    borderTopLeftRadius: 0,
    top: 'unset',
    '&:hover $expandCollapse': {
      display: 'flex'
    },
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen
      })
    },
    zIndex: theme.zIndex.drawer + 1
  },
  drawerPaperPermanent: {
    overflowX: 'hidden',
    WebkitTransition: 'width 0.85s ease',
    '-moz-transition': 'width 0.85s ease',
    '-ms-transition': 'width 0.85s ease',
    '-o-transition': 'width 0.85s ease',
    transition: 'width 0.85s ease',
    width: theme.spacing(8),
    borderRight: `1px solid ${theme.palette.grey[300]}`,
    '&:hover': {
      width: theme.spacing(30),
      borderRight: `1px solid ${theme.palette.grey[300]}`,
      boxShadow: `inset -10px 0px 5px -10px ${alpha(
        theme.palette.common.black,
        0
      )}, 0px 2px 4px -1px ${alpha(
        theme.palette.common.black,
        0.2
      )}, 0px 4px 5px 0px ${alpha(
        theme.palette.common.black,
        0.14
      )}, 0px 1px 10px 0px ${alpha(theme.palette.common.black, 0.12)}`
    }
  },
  drawerPaperPinned: {
    width: theme.spacing(30),
    borderRight: `1px solid ${theme.palette.grey[300]}`,
    '&:hover': {
      width: theme.spacing(30),
      boxShadow: `inset -10px 0px 5px -10px ${alpha(
        theme.palette.common.black,
        0.15
      )}`
    }
  },
  expandCollapse: {
    display: ({ pinned }: { pinned: boolean }) => (pinned ? 'flex' : 'none'),
    position: 'absolute',
    top: theme.spacing(3),
    right: theme.spacing(1),
    width: theme.spacing(4),
    height: theme.spacing(4),
    color: ({ pinned }: { pinned: boolean }) =>
      pinned ? theme.palette.primary.main : theme.palette.grey[700]
  },
  divider: {
    marginBottom: theme.spacing(1)
  },
  addNote: {
    margin: theme.spacing(8, 0, 2, 1),
    display: 'flex',
    justifyContent: 'space-between'
  },
  buttonTitle: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    position: 'absolute',
    left: theme.spacing(6.5),
    opacity: ({ pinned, drawerOpen }: PageStyleProps) =>
      drawerOpen || pinned ? 1 : 0
  },

  smoothOpenAnimation: {
    '-webkit-transition': theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.enteringScreen,
      delay: 500
    }),
    '-moz-transition': theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.enteringScreen,
      delay: 500
    }),
    '-ms-transition': theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.enteringScreen,
      delay: 500
    }),
    '-o-transition': theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.enteringScreen,
      delay: 500
    }),
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.enteringScreen,
      delay: 500
    })
  },
  closeAnimation: {
    '-webkit-transition': 'opacity 1s linear',
    '-moz-transition': 'opacity 1s linear',
    '-ms-transition': 'opacity 1s linear',
    '-o-transition': 'opacity 1s linear',
    transition: 'opacity 1s linear',
    '-webkit-transition-delay': '.25s',
    '-moz-transition-delay': '.25s',
    '-ms-transition-delay': '.25s',
    '-o-transition-delay': '.25s',
    'transition-delay': '.25s',
    opacity: ({ pinned, drawerOpen }: PageStyleProps) =>
      drawerOpen || pinned ? 1 : 0
  },
  expand: {
    position: 'absolute',
    margin: theme.spacing(0.5, 0, 0, 0),
    transition: 'all 225ms cubic-bezier(0.4, 0, 0.6, 1) 500ms',
    opacity: ({ pinned, drawerOpen }: PageStyleProps) =>
      drawerOpen || pinned ? 1 : 0
  },
  collaps: {
    position: 'absolute',
    margin: theme.spacing(0, 0, 0, 0),
    transition: 'all 225ms cubic-bezier(0.4, 0, 0.6, 1) 500ms',
    opacity: ({ pinned, drawerOpen }: PageStyleProps) =>
      drawerOpen || pinned ? 0 : 1
  },
  list: {
    position: 'absolute',
    bottom: theme.spacing(8.5),
    padding: theme.spacing(0),
    width: '100%'
  }
}));

const useButtonStyles = makeStyles((theme) => ({
  root: {
    minWidth: theme.spacing(6),
    width: ({ pinned, drawerOpen }: PageStyleProps) =>
      theme.spacing(drawerOpen || pinned ? 14 : 6),
    height: theme.spacing(6),
    borderRadius: theme.spacing(6),
    padding: 0,
    '-webkit-border-radius': theme.spacing(6),
    '-moz-border-radius': theme.spacing(6),
    '-webkit-transition': theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.enteringScreen,
      delay: 500
    }),
    '-moz-transition': theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.enteringScreen,
      delay: 500
    }),
    '-ms-transition': theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.enteringScreen,
      delay: 500
    }),
    '-o-transition': theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.enteringScreen,
      delay: 500
    }),
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.enteringScreen,
      delay: 500
    })
  },
  startIcon: {
    position: 'absolute',
    left: theme.spacing(2.25)
  }
}));

const addIcon = <AddIcon data-testid={'add-icon'} />;

export const SidePanel: React.FC = () => {
  const { pinned = true, togglePinned } = useSidePanelContext();
  const [drawerOpen, setDrawerOpen] = useState<boolean>(pinned);
  const classes = useStyles({ pinned, drawerOpen });
  const buttonClasses = useButtonStyles({ pinned, drawerOpen });

  const openDrawer = useCallback(async () => {
    if (!pinned) {
      setDrawerOpen(true);
    }
  }, [pinned]);

  const closeDrawer = useCallback(async () => {
    if (!pinned) {
      setDrawerOpen(false);
    }
  }, [pinned]);

  const drawerClasses = useMemo(
    () => ({
      paper: classNames(
        classes.drawerPaper,
        classes.drawerPaperPermanent,
        classes.smoothOpenAnimation,
        {
          [classes.drawerPaperPinned]: pinned
        }
      )
    }),
    [
      classes.drawerPaper,
      classes.drawerPaperPermanent,
      classes.drawerPaperPinned,
      pinned,
      classes.smoothOpenAnimation
    ]
  );

  const {
    open: openAddNote,
    openDialog: openAddNoteDialog,
    closeDialog: closeAddNoteDialog
  } = useDialogState();
  const onAddNoteClick = useCallback(() => {
    closeDrawer();
    openAddNoteDialog();
  }, [closeDrawer, openAddNoteDialog]);
  const { isAutoPilotEnabled } = useAutoPilotContext();

  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');
  const addNote = useMessage('note.addNote');
  const pinnedBtn = useMessage('pinnedMsg');

  return (
    <>
      <Drawer
        data-testid="drawer-root"
        onMouseOver={openDrawer}
        onMouseLeave={closeDrawer}
        classes={drawerClasses}
        variant="permanent"
        open={drawerOpen || pinned}
      >
        <div data-testid="zt-expandLogo" className={classes.expand}>
          <ZT_logo_Expanded />
        </div>

        <div data-testid="zt-collapsLogo" className={classes.collaps}>
          <ZT_logo_Collapsed />
        </div>

        <div className={classes.addNote}>
          <Button
            classes={buttonClasses}
            variant="contained"
            startIcon={addIcon}
            onClick={onAddNoteClick}
            data-testid="add-note-btn"
            aria-label={isTiwScreenReaderEnabled ? addNote : ''}
          >
            <Typography
              variant="button"
              className={classNames(
                classes.buttonTitle,
                classes.closeAnimation
              )}
            >
              <FormattedMessage id="notes.note" />
            </Typography>
          </Button>

          {(drawerOpen || pinned) && (
            <IconButton
              onClick={togglePinned}
              className={classes.closeAnimation}
              data-testid="drawer-pin-btn"
              aria-label={isTiwScreenReaderEnabled ? pinnedBtn : ''}
            >
              <PinIcon color={pinned ? 'primary' : 'inherit'} />
            </IconButton>
          )}
        </div>

        <SidePanelOptions />
        {isAutoPilotEnabled && (
          <List className={classes.list}>
            <AutoPilotEnabled />
          </List>
        )}
        <UserProfile setDrawerOpen={setDrawerOpen} />
      </Drawer>
      {openAddNote && (
        <NoteAnimationContextProvider>
          <TimesheetLabelSuggestionContextProvider>
            <AddNoteDialog open={openAddNote} onClose={closeAddNoteDialog} />
          </TimesheetLabelSuggestionContextProvider>
        </NoteAnimationContextProvider>
      )}
      <NoteToast pinned={pinned} />
    </>
  );
};
